import { Button } from 'iglooform';
import { useSelector, useDispatch, useParams, history, useLocation } from 'umi';
import { useMount, useUnmount } from 'ahooks';
import { Row, Col, Space, message } from 'antd';
import classnames from 'classnames';
import moment from 'moment';
import qs from 'querystring';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { useIntl, FormattedMessage } from 'react-intl';
import numeral from 'numeral';
import { get } from 'lodash';
import { images } from '@/assets/images';
import ListItemPanel from '@/components/list-item-panel';
import AmountWithUnit from '@/components/amount-with-unit';
import Amount from '@/components/amount';
import ListItem from '@/components/list-item';
import PageFrame from '@/components/page-frame';
import Panel from '@/components/panel';
import Divider from './components/divider';
import useMobile from '@/hooks/useMobile';
import { isUndefined } from '@/utils/common';
import premiumConfig from '@/configs/premium';
import useUpdateRemoteLangMessage from '@/hooks/useUpdateRemoteLangMessage';
import useVehicleFiledsVisiblity from '@/hooks/useVehicleFiledsVisiblity';
import specialIntlMessages from '@/utils/specialIntlMessages';
import SkeletonLoading from './components/skeleton-loading';
import * as SESSION_KEYS from '@/configs/session-storage-keys';
import styles from './index.less';

const Share = () => {
  const { shareInfo, planCmsDetailInfo, loading } = useSelector<
    ReduxState,
    ShareState & { loading: boolean }
  >((state) => ({
    ...state.share,
    loading: state.loading.effects['share/getShareInfo'],
  }));

  const isMobile = useMobile();

  const location: any = useLocation();
  const params = useParams<{ type: TTypes }>();
  const { formatMessage, locale } = useIntl();
  const dispatch = useDispatch();

  let {
    share_id: shareId,
    share_url: shareUrl,
    replace_pathname: replacePathname,
  } = location.query;

  const shareUrlAry = shareUrl.split('?');
  if (shareUrlAry && shareUrlAry.length === 2) {
    const [sharePath, shareQueryStr] = shareUrlAry;
    const shareQuery = qs.parse(shareQueryStr);
    shareUrl = `${sharePath}?${qs.stringify({ ...shareQuery, lang: locale })}`;
  }

  shareUrl = decodeURIComponent(shareUrl);

  const fastQuote = shareInfo?.fast_quote;
  const note = fastQuote?.note;
  const includePa = fastQuote?.include_pa === 'Yes';
  const productType = params.type;

  let { coverage_type: coverageType } = planCmsDetailInfo;
  coverageType = coverageType ?? productType;

  const visibilities = useVehicleFiledsVisiblity({
    vehicleType: fastQuote?.vehicle_type,
  });

  useMount(() => {
    dispatch({
      type: 'share/getShareInfo',
      payload: {
        productType,
        shareId,
      },
    });
  });

  useUnmount(() => {
    sessionStorage.removeItem(SESSION_KEYS.QUOTE_INFO);
    sessionStorage.removeItem(SESSION_KEYS.FIRST_STEP_INFO_FAST_QUOTE);
    sessionStorage.removeItem(SESSION_KEYS.PICKED_PLAN_INFO);
  });

  useUpdateRemoteLangMessage([
    () =>
      dispatch({
        type: 'share/getShareInfo',
        payload: {
          productType,
          shareId,
        },
      }),
  ]);

  const renderQuoteInfo = () => {
    return (
      <Panel
        className={classnames([styles.panel, styles.panelQuote])}
        title={
          <span className={classnames([styles.title, styles.titleLevel1])}>
            {formatMessage({
              id: 'Quote',
            })}
          </span>
        }
        subTitle={shareInfo?.share_id}
      >
        <Panel.ListItem
          label={formatMessage({
            id: 'Quote Expiry',
          })}
        >
          {moment(shareInfo?.expire_time).format('DD / MM / YYYY')}
        </Panel.ListItem>
      </Panel>
    );
  };

  const renderVehicleInfo = () => {
    const cardTitleMaps = {
      motor:
        planCmsDetailInfo?.soi_category === 'motorcycle'
          ? formatMessage({
              id: 'Motorcycle Information',
            })
          : formatMessage({
              id: 'Vehicle Information',
            }),
      travel: formatMessage({
        id: 'Travel Details',
      }),
      pa: formatMessage({
        id: 'Policy Details',
      }),
      health: formatMessage({
        id: 'Policy Details',
      }),
    };
    const iconMaps = {
      motor:
        planCmsDetailInfo?.soi_category === 'motorcycle'
          ? images.motorcycle_icon
          : images.vehicle_icon,
      travel: images.travel_icon,
      pa: images.pa_icon,
      health: images.hc_icon,
    };
    const foldCardList = [
      // motor
      {
        label: formatMessage({ id: 'Vehicle Type' }),
        value: fastQuote?.vehicle_type ? (
          <FormattedMessage {...specialIntlMessages[fastQuote?.vehicle_type]} />
        ) : null,
        hidden: isUndefined(fastQuote?.vehicle_type),
      },
      {
        label: formatMessage({ id: 'Business Usage' }),
        value: fastQuote?.business_usage ? (
          <FormattedMessage
            {...specialIntlMessages[fastQuote?.business_usage]}
          />
        ) : null,
        hidden: !visibilities.business,
      },
      {
        label: formatMessage({ id: 'Number of Seats' }),
        value: fastQuote?.seats_number,
        hidden: !visibilities.numberOfSeas,
      },
      {
        label: formatMessage({ id: 'Vehicle Value' }),
        value: (
          <AmountWithUnit
            amount={fastQuote?.vehicle_value ?? 0}
            unit={formatMessage({ id: 'VND' })}
          />
        ),
        hidden: isUndefined(fastQuote?.vehicle_value),
      },
      {
        label: formatMessage({ id: 'Vehicle Origin' }),
        value: fastQuote?.vehicle_origin ? (
          <FormattedMessage
            {...specialIntlMessages[fastQuote?.vehicle_origin]}
          />
        ) : null,
        hidden: isUndefined(fastQuote?.vehicle_origin),
      },
      {
        label: formatMessage({ id: 'Vehicle Manufacture Date' }),
        value: fastQuote?.manufacture_date
          ? moment(fastQuote?.manufacture_date).format('DD / MM / YYYY')
          : undefined,
        hidden:
          isUndefined(fastQuote?.manufacture_date) ||
          fastQuote?.manufacture_date === 0,
      },
      {
        label: formatMessage({ id: 'Vehicle Registration Date' }),
        value: fastQuote?.registration_date
          ? moment(fastQuote?.registration_date).format('MM / YYYY')
          : undefined,
        hidden:
          isUndefined(fastQuote?.registration_date) ||
          fastQuote?.registration_date === 0,
      },
      // travel
      {
        label: formatMessage({ id: 'Destination' }),
        value: fastQuote?.destination ? (
          <FormattedMessage {...specialIntlMessages[fastQuote?.destination]} />
        ) : null,
        hidden: isUndefined(fastQuote?.destination),
      },
      {
        label: formatMessage({ id: 'Travel Start Date' }),
        value: fastQuote?.travel_start_date
          ? moment(fastQuote?.travel_start_date).format('DD / MM / YYYY')
          : undefined,
      },
      {
        label: formatMessage({ id: 'Travel End Date' }),
        value: fastQuote?.travel_end_date
          ? moment(fastQuote?.travel_end_date).format('DD / MM / YYYY')
          : undefined,
      },
      // hospital cash
      {
        label: formatMessage({ id: 'Policy Duration' }),
        value: fastQuote?.policy_duration ? (
          <FormattedMessage
            {...specialIntlMessages[fastQuote?.policy_duration]}
          />
        ) : null,
        hidden: isUndefined(fastQuote?.policy_duration),
      },
      {
        label: formatMessage({ id: 'No. of Insured' }),
        value: fastQuote?.insured_num ? fastQuote?.insured_num : null,
        hidden: isUndefined(fastQuote?.insured_num),
      },
      // pa
      {
        label: formatMessage({ id: 'Plan' }),
        value: fastQuote?.applicants ? (
          <FormattedMessage {...specialIntlMessages[fastQuote?.applicants]} />
        ) : null,
        hidden: isUndefined(fastQuote?.applicants),
      },
    ].filter((e) => !isUndefined(e.value) && !e.hidden);
    return (
      <Panel
        className={classnames([styles.panel, styles.panelVehicle])}
        title={
          <div className={styles.vehicleTitleWrap}>
            <img className={styles.titleIcon} src={iconMaps[productType]} />
            <span className={classnames([styles.title, styles.titleLevel2])}>
              {cardTitleMaps[productType]}
            </span>
          </div>
        }
      >
        {foldCardList.map((item) => (
          <Panel.ListItem
            className={styles.listItem}
            key={item.label}
            label={item.label}
          >
            {item.value}
          </Panel.ListItem>
        ))}
      </Panel>
    );
  };

  const renderNote = () => {
    return (
      <Panel
        className={classnames([styles.panel, styles.panelNote])}
        title={
          <span className={classnames([styles.title, styles.titleLevel3])}>
            {formatMessage({
              id: 'Note',
            })}
          </span>
        }
      >
        <div className={styles.PanelNoteContent}>{note}</div>
      </Panel>
    );
  };

  const renderMotorPlan = () => {
    const plan = shareInfo?.Plan;
    const benefits = planCmsDetailInfo?.cms?.data?.attributes?.benefits ?? [];
    const insurer = planCmsDetailInfo.cms?.data?.attributes?.insurer || {};
    const logoUrl =
      insurer?.data?.attributes?.logo?.data?.attributes?.url ||
      plan?.details?.logo_url;
    const planName =
      planCmsDetailInfo.cms?.data?.attributes?.planName || plan?.name;

    const benefitsFieldList = benefits.map((item: { [prop: string]: any }) => {
      return {
        label: item?.text,
        value: item?.coverage,
        children: item.subItems.map((e: { [prop: string]: any }) => {
          return {
            label: e.text,
            value: e?.coverage,
          };
        }),
      };
    });

    const showCoverageColumn = benefitsFieldList.some((item: any) => {
      return item.value || item?.children?.some((e: any) => e.value);
    });

    const planInfoList = [
      {
        label: formatMessage({ id: 'Benefit' }),
        type: 'strong',
        value: showCoverageColumn ? formatMessage({ id: 'Coverage' }) : null,
      },
      ...benefitsFieldList,
      {
        label: formatMessage({ id: 'Add-ons Benefits' }),
        value: !includePa ? formatMessage({ id: '(None Selected)' }) : null,
        children: includePa
          ? planCmsDetailInfo?.soi_category === 'motorcycle'
            ? [
                {
                  label: formatMessage({
                    id: 'Personal Accident',
                  }),
                  value: formatMessage({
                    id: 'Up to VND 10.000.000',
                  }),
                },
              ]
            : [
                {
                  label: formatMessage({
                    id: 'Personal Accident (Passenger)',
                  }),
                  value: formatMessage({
                    id: 'Up to VND 150.000.000',
                  }),
                },
                {
                  label: formatMessage({
                    id: 'Personal Accident (Driver)',
                  }),
                  value: formatMessage({
                    id: 'Up to VND 10.000.000',
                  }),
                },
              ]
          : [],
        hidden:
          coverageType === 'cpd' ||
          ['travel', 'health', 'pa'].includes(productType),
      },
    ].filter((e) => !e.hidden);

    const coverage =
      productType === 'health' && fastQuote?.policy_duration
        ? formatMessage(
            {
              id: '{duration} Coverage',
            },
            {
              duration: (
                <FormattedMessage
                  {...specialIntlMessages[fastQuote?.policy_duration]}
                />
              ),
            },
          )
        : undefined;

    return (
      <ListItem
        icon={logoUrl}
        title={planName}
        list={planInfoList}
        coverage={coverage}
      />
    );
  };

  const renderMotorPremiumDetailPanel = () => {
    const premiumInfo: { [prop: string]: any } = shareInfo?.meta;
    const specificPremiumConfig = premiumConfig[productType][coverageType];

    const basicPremium = get(premiumInfo, specificPremiumConfig?.basicPremium);
    const paPremium = get(premiumInfo, specificPremiumConfig?.paPremium, 0);
    const vatPremium = get(
      premiumInfo,
      specificPremiumConfig?.vatPremium,
      null,
    );
    const grossPremium = numeral(basicPremium).add(paPremium).value() as number;
    const amountPayable = get(
      premiumInfo,
      specificPremiumConfig?.amountPayable,
      0,
    );

    const list = [
      {
        label: formatMessage({
          id: 'Basic Premium',
        }),
        value: <Amount amount={basicPremium} />,
      },
      {
        label: formatMessage({
          id: 'Add-ons Sub-total',
        }),
        value: <Amount amount={paPremium} />,
        children:
          paPremium > 0
            ? [
                {
                  label: formatMessage({ id: 'Personal Accident' }),
                  value: <Amount amount={paPremium} />,
                },
              ]
            : [],
        hidden:
          coverageType === 'cpd' ||
          ['travel', 'health', 'pa'].includes(productType),
      },
      {
        label: formatMessage({
          id: 'Total Gross Premium',
        }),
        value: <Amount amount={grossPremium} />,
        type: 'strong',
      },
      {
        label: formatMessage({
          id: 'VAT(10%)',
        }),
        value: <Amount amount={vatPremium} />,
        type: 'smallSpace',
        hidden: vatPremium === null,
      },
      {
        label: formatMessage({
          id: 'Amount Payable',
        }),
        value: <Amount amount={amountPayable} />,
        type: 'total',
        awalysShow: true,
      },
    ].filter((e) => !e.hidden) as any[];

    return (
      <ListItemPanel
        className={styles.listItemPanel}
        title={formatMessage({
          id: 'Premium Details',
        })}
        subTitle={formatMessage({
          id: 'All premiums in Vietnamese Dong',
        })}
        list={list}
      />
    );
  };

  return (
    <PageFrame
      title={formatMessage({
        id: 'Share',
      })}
      onBack={() => {
        if (replacePathname) {
          history.replace(decodeURIComponent(replacePathname));
          return;
        }
        history.goBack();
      }}
    >
      {shareInfo?.meta && !loading ? (
        <div
          className={classnames([
            styles.content,
            {
              [styles['is-mobile']]: isMobile,
            },
          ])}
        >
          <Row>
            <Col span={isMobile ? 24 : 12}>
              {renderQuoteInfo()}
              <Divider
                style={isMobile ? { margin: '24px 0' } : { margin: '32px 0' }}
              />
              {renderVehicleInfo()}
              {note && (
                <>
                  <Divider
                    style={
                      isMobile ? { margin: '24px 0' } : { margin: '32px 0' }
                    }
                  />
                  {renderNote()}
                </>
              )}
              <Divider
                style={isMobile ? { margin: '24px 0' } : { margin: '32px 0' }}
              />
              {renderMotorPlan()}
            </Col>
            <Col span={isMobile ? 24 : 12}>
              {renderMotorPremiumDetailPanel()}
            </Col>
          </Row>
          <Space
            className={styles.buttonList}
            size={24}
            direction={isMobile ? 'vertical' : 'horizontal'}
          >
            {/* <Button
              type="primary"
              className="ignite-btn"
              href={`https://api.whatsapp.com/send?text=${encodeURIComponent(
                shareUrl!,
              )}`}
            >
              {formatMessage({
                id: 'Share via Zalo',
              })}
            </Button> */}
            <CopyToClipboard
              onCopy={() => {
                message.info({
                  className: 'ignite-message-notice',
                  content: formatMessage({
                    id: 'Quote Link Copied.',
                  }),
                });

                //event tracking: share quote
                window.gtag('event', 'share_quote', {
                  page: 'quote_view',
                  channel: 'link',
                  product_key: shareInfo?.plan_key,
                  plan_key: shareInfo?.product_key,
                  user_unique_id: JSON.parse(
                    localStorage.getItem('user_info') ?? '{}',
                  )?.id,
                });
              }}
              text={shareUrl!}
            >
              <Button
                className="ignite-btn"
                style={!isMobile ? { width: 217 } : {}}
              >
                {formatMessage({
                  id: 'Copy Quote Link',
                })}
              </Button>
            </CopyToClipboard>
          </Space>
        </div>
      ) : (
        <SkeletonLoading />
      )}
    </PageFrame>
  );
};

export default Share;
